import api from '@/api';
import type { components } from '@/api/qraft/schema';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_PAGE_SIZE,
} from '~/hooks/useAsyncPeoplePicker';

export type AssignCandidate = components['schemas']['AssignCandidate'];

/** The people picker debounce, re-exported so the modal and the hook agree. */
export { PEOPLE_PICKER_DEBOUNCE_MS };

export interface AssignCandidatesResult {
  candidates: AssignCandidate[];
  /** Total matching the current search, not the length of the page. */
  count: number;
  isFetching: boolean;
  /**
   * A refusal from the endpoint's own gate, e.g. a 404 on an unassignable
   * target.
   */
  error: unknown;
}

/**
 * The assign picker's roster: the admin and mod leaders of the group behind the
 * selected target, minus the caller.
 *
 * The target ids are query parameters, so changing the target refetches the
 * roster, as legacy's reset-on-change does. The server checks the posted group
 * against the student's own matches rather than trusting it.
 *
 * @param studentId the student the modal is open for, or `null` when none is.
 *
 * @param campaignId the posted campaign, when the student has a matched one.
 *
 * @param webAdId the posted web target, when there is no campaign.
 *
 * @param search the debounced search term; `''` asks for the first page.
 */
export function useAssignCandidates(
  studentId: number | null,
  campaignId: number | undefined,
  webAdId: number | undefined,
  search: string,
): AssignCandidatesResult {
  const hasTarget = campaignId !== undefined || webAdId !== undefined;

  const { data, isFetching, error } = api.getAssignCandidates.useQuery(
    {
      path: { student_id: studentId ?? 0 },
      query: {
        campaign_id: campaignId,
        web_ad_id: webAdId,
        search: search === '' ? undefined : search,
        limit: PEOPLE_PICKER_PAGE_SIZE,
      },
    },
    // `retry: false`, as in `useStudentAdoptionInfo`: a typed 404 for a target
    // the caller can't assign through is an answer, not a fault.
    { enabled: studentId !== null && hasTarget, retry: false },
  );

  return {
    candidates: data?.data ?? [],
    count: data?.count ?? 0,
    isFetching,
    error,
  };
}
